import React, { useState } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useUtils } from '@mui/x-date-pickers/internals';
import { isAfter, isBefore, isSameDay, isValid } from 'date-fns';
import ReportsCalendarHeader from './ReportsCalendarHeader';

const VIEWS = ['year', 'month', 'day'];
const BAND_RADIUS = 18; // half of PickersDay's 36px height, so band ends are round

// Draft dates can be null or an Invalid Date while a field is half-typed.
const isDate = (value) => value instanceof Date && isValid(value);

// Start active: the day becomes the start (end kept unless now before it), then
// End becomes active. End active: the day becomes the end, or the new start if
// it's before the current start.
const applyDayPick = (start, end, activeField, day) => {
  if (activeField === 'start') {
    const keepEnd = isDate(end) && !isAfter(day, end);
    return { start: day, end: keepEnd ? end : null, activeField: 'end' };
  }
  if (isDate(start) && isBefore(day, start)) return { start: day, end: null, activeField: 'end' };
  return { start, end: day, activeField: 'end' };
};

// The wrapper paints the range band behind PickersDay, across its side margins
// too, so the band runs unbroken along a week row.
const RangeDay = ({ rangeStart, rangeEnd, bandFrom, bandTo, isPreview, onHoverDay, ...dayProps }) => {
  const theme = useTheme();
  const utils = useUtils();
  const { day, outsideCurrentMonth, isFirstVisibleCell, isLastVisibleCell } = dayProps;

  const isEndpoint = (isDate(rangeStart) && isSameDay(day, rangeStart))
    || (isDate(rangeEnd) && isSameDay(day, rangeEnd));
  const inBand = Boolean(bandFrom && bandTo) && !outsideCurrentMonth
    && !isBefore(day, bandFrom) && !isAfter(day, bandTo);

  let bandSx = {};
  if (inBand) {
    const color = alpha(theme.palette.primary.main, isPreview ? 0.07 : 0.14);
    const isBandStart = isSameDay(day, bandFrom);
    const isBandEnd = isSameDay(day, bandTo);
    // Band ends only fill the half that faces the rest of the range.
    const background = isBandStart
      ? `linear-gradient(to right, transparent 50%, ${color} 50%)`
      : isBandEnd
        ? `linear-gradient(to left, transparent 50%, ${color} 50%)`
        : color;
    // Round the band off where it meets the edge of a week row or the month.
    const roundLeft = !isBandStart && (isFirstVisibleCell || isSameDay(day, utils.startOfWeek(day)));
    const roundRight = !isBandEnd && (isLastVisibleCell || isSameDay(day, utils.endOfWeek(day)));
    bandSx = {
      background,
      ...(roundLeft && { borderTopLeftRadius: BAND_RADIUS, borderBottomLeftRadius: BAND_RADIUS }),
      ...(roundRight && { borderTopRightRadius: BAND_RADIUS, borderBottomRightRadius: BAND_RADIUS }),
    };
  }

  return (
    <Box role="presentation" sx={{ display: 'flex', ...bandSx }}>
      <PickersDay
        {...dayProps}
        // DayCalendar only marks its single `value` as selected; the range decides here.
        selected={isEndpoint}
        aria-selected={isEndpoint || (inBand && !isPreview)}
        onPointerEnter={(event) => {
          dayProps.onPointerEnter?.(event);
          onHoverDay(day);
        }}
      />
    </Box>
  );
};

// Controlled single-month range calendar: onChange(start, end, nextActiveField).
const ReportsRangeCalendar = ({ start, end, activeField, onChange, minDate, maxDate }) => {
  const [hoveredDay, setHoveredDay] = useState(null);
  const hasStart = isDate(start);
  const hasEnd = isDate(end);

  // While the active field is empty, preview the range up to the hovered day.
  let band = { from: null, to: null, isPreview: false };
  if (activeField === 'end' && hasStart && !hasEnd && isDate(hoveredDay) && isAfter(hoveredDay, start)) {
    band = { from: start, to: hoveredDay, isPreview: true };
  } else if (activeField === 'start' && !hasStart && hasEnd && isDate(hoveredDay) && isBefore(hoveredDay, end)) {
    band = { from: hoveredDay, to: end, isPreview: true };
  } else if (hasStart && hasEnd && isBefore(start, end)) {
    band = { from: start, to: end, isPreview: false };
  }

  // The calendar shows its value's month, so the active date's month comes into view.
  const activeDate = activeField === 'start' ? start : end;
  const otherDate = activeField === 'start' ? end : start;
  const calendarValue = isDate(activeDate) ? activeDate : isDate(otherDate) ? otherDate : null;

  // Year/month picks only navigate, but MUI builds the month grid from `value`'s
  // year, so the navigated date stands in as the value until the selection moves.
  const [navigated, setNavigated] = useState(null);
  const anchor = calendarValue ? calendarValue.getTime() : null;
  const shownDate = navigated && navigated.anchor === anchor ? navigated.date : calendarValue;

  const handleChange = (day, selectionState) => {
    if (!isDate(day)) return;
    // Month/year picks report a date as 'partial'; only day picks are 'finish'.
    if (selectionState !== 'finish') {
      setNavigated({ anchor, date: day });
      return;
    }
    const next = applyDayPick(start, end, activeField, day);
    onChange(next.start, next.end, next.activeField);
  };

  return (
    <Box onPointerLeave={() => setHoveredDay(null)}>
      <DateCalendar
        value={shownDate}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        views={VIEWS}
        openTo="day"
        slots={{ calendarHeader: ReportsCalendarHeader, day: RangeDay }}
        slotProps={{
          day: {
            rangeStart: start,
            rangeEnd: end,
            bandFrom: band.from,
            bandTo: band.to,
            isPreview: band.isPreview,
            onHoverDay: setHoveredDay,
          },
        }}
        sx={{ width: '100%', maxWidth: 320 }}
      />
    </Box>
  );
};

export default ReportsRangeCalendar;
